// Small in-panel confirm dialog. Widget-local on purpose: the dashboard SPA
// never needs it (its links navigate in place), so it doesn't belong in
// chat-core. The scrim is absolutely positioned inside .nw-panel — dimming
// the customer's host page is not this widget's call to make.
import { useEffect, useRef } from 'react'
import { useOutsidePress } from '@netwatchai/chat-core'

export interface ConfirmDialogProps {
  title: string
  message: string
  confirmLabel: string
  cancelLabel?: string
  // When set, the primary action becomes a plain link (popup was blocked and
  // the pre-opened tab is gone — a real anchor is the one thing blockers
  // always allow). The href carries the already-minted handoff token.
  fallbackHref?: string | null
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  fallbackHref,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const primaryRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null)
  useOutsidePress([cardRef], onCancel, true)

  // Escape + a real focus trap: aria-modal only *claims* the rest of the panel
  // is inert, so Tab has to be kept inside the card for that to be true. The
  // element the dialog was opened from is refocused on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
        return
      }
      if (e.key !== 'Tab' || !cardRef.current) return
      const focusable = cardRef.current.querySelectorAll<HTMLElement>('button, a[href]')
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      // The active element inside a shadow root reports as the host, so read
      // the card's own root to find where focus really is.
      const root = cardRef.current.getRootNode() as Document | ShadowRoot
      const active = root.activeElement
      if (e.shiftKey && (active === first || !cardRef.current.contains(active as Node))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      opener?.focus?.()
    }
  }, [onCancel])

  // Focus the primary action on mount AND when it swaps button↔link (the
  // blocked-popup fallback replaces the node, which would drop focus to body).
  useEffect(() => {
    primaryRef.current?.focus()
  }, [fallbackHref])

  return (
    <div className="nw-confirm-dim">
      <div ref={cardRef} className="nw-confirm" role="dialog" aria-modal="true" aria-label={title}>
        <div className="nw-confirm-title">{title}</div>
        <div className="nw-confirm-msg">{message}</div>
        <div className="nw-confirm-actions">
          <button type="button" className="nw-confirm-btn" onClick={onCancel}>
            {cancelLabel}
          </button>
          {fallbackHref ? (
            <a
              ref={primaryRef as React.RefObject<HTMLAnchorElement>}
              className="nw-confirm-btn is-primary"
              href={fallbackHref}
              target="_blank"
              rel="noreferrer"
              onClick={onCancel}
            >
              Open dashboard →
            </a>
          ) : (
            <button
              ref={primaryRef as React.RefObject<HTMLButtonElement>}
              type="button"
              className="nw-confirm-btn is-primary"
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
